<template>
    <div>
        <Alert
            v-if="authStore.isAuthenticated !== true"
            type="warning">
            Funktion für unangemeldete Nutzer noch in der Entwicklung
        </Alert>
        <div v-else>
            <div>
                <div class="mb-8">
                    <div
                        v-if="addsToExistingProcess"
                        class="text-sm mb-2">
                        Formular hinzufügen zur Maßnahme aus dem Jahr
                        <span class="font-semibold">{{ yearId }}</span>
                    </div>
                    <div
                        v-else
                        class="text-sm mb-2 flex items-center gap-x-2">
                        Maßnahme erstellen für Jahr:
                        <span
                            v-if="!changeYear"
                            class="font-semibold">{{ yearId }}</span>
                        <span
                            v-if="!changeYear && canChangeYear"
                            class="text-xs cursor-pointer text-primary-500"
                            @click="changeYear = true">ändern</span>
                        <div class="inline-block w-[120px]">
                            <BaseSelect
                                v-if="changeYear"
                                v-model="yearId"
                                name="yearId"
                                display-key="id"
                                :options="selectableYears" />
                        </div>
                    </div>
                    <template v-if="!addsToExistingProcess">
                        <Alert
                            v-if="formsSelectable"
                            type="info">
                            Das Jahr richtet sich nach dem Beginn der Maßnahme. Beginnt die Maßnahme z. B. im Dezember
                            {{ new Date().getFullYear() }}, ist das Jahr {{ new Date().getFullYear() }} auszuwählen – auch
                            wenn die Maßnahme erst im folgenden Jahr endet.
                        </Alert>
                        <Alert
                            v-else
                            type="info">
                            Für das Jahr {{ yearId }} ist die Erstellung von Maßnahmen deaktiviert.
                            Bitte wählen Sie ein anderes Jahr aus, für das die Erstellung von Maßnahmen aktiviert ist.
                        </Alert>
                        <Alert
                            v-if="formsSelectable && disabledFundingAreaNames.length > 0"
                            class="mt-2"
                            type="info">
                            Für folgende Förderbereiche können im Jahr {{ yearId }} keine neuen Maßnahmen angelegt
                            werden: {{ disabledFundingAreaNames.join(', ') }}
                        </Alert>
                    </template>
                </div>


                <div
                    v-if="(!slideOverStore.context || slideOverStore.context.type === 'event') && formsSelectable">
                    <label
                        for="default-search"
                        class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">{{
                            $t('fundingPrograms.slideOver.search')
                        }}</label>
                    <div class="relative mb-3">
                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg
                                class="w-4 h-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 20">
                                <path
                                    stroke="currentColor"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                            </svg>
                        </div>
                        <input
                            id="default-search"
                            v-model="search"
                            type="search"
                            class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                            :placeholder="$t('fundingPrograms.slideOver.searchPlaceholder')"
                            required>
                    </div>
                </div>
            </div>
            <FundingProgramsAccordion
                v-if="formsSelectable"
                class="p-2"
                :parent-funding-programs="parentFundingPrograms"
                :child-funding-programs="childFundingPrograms"
                :loading="loading"
                @add-new-form-process="addNewFormProcess" />
        </div>
    </div>
</template>

<script setup>
import debounce from "lodash/debounce"
import {useNuxtApp} from "nuxt/app"
import {useI18n} from "vue-i18n"
import {useNotification} from '@/composables/useNotification'
import {useAuthStore} from "@/store/auth"
import {useSlideOverStore} from "@/store/slideOver"
import {useStatic} from "@/store/static"
import {$lara, $larafetch} from '@/utils/$larafetch'

const router = useRouter()
const {t: $t} = useI18n()
const notification = useNotification()
const slideOverStore = useSlideOverStore()
const authStore = useAuthStore()
const staticStore = useStatic()
const {fundingAreaIdOf, enabledFundingAreaIds, isProcessCreationEnabled} = useYearFundingAreas()

const fundingPrograms = ref([])
const search = ref('')
const loading = ref(0)
const changeYear = ref(false)
const yearId = ref(null)

// The year and funding area switches only restrict applicants starting a new process. Inside
// an existing process only forms are added, and the management area may always start one.
const restrictsProcessCreation = computed(() => slideOverStore.context?.type !== 'process'
    && slideOverStore.context?.area !== 'management')

const addsToExistingProcess = computed(() => slideOverStore.context?.type === 'process')

// Forms in an existing process or event get the year of the process or event (the backend
// enforces this as well), the year selection only applies to completely new processes.
const contextYearId = computed(() => ['process', 'event'].includes(slideOverStore.context?.type)
    ? slideOverStore.context?.data?.year_id ?? null
    : null)

const selectedYear = computed(() => staticStore.years?.find(year => year.id === yearId.value))

const disabledFundingAreaNames = computed(() => {
    if (!restrictsProcessCreation.value) return []

    return (selectedYear.value?.fundingAreas ?? [])
        .filter(fundingArea => !fundingArea.process_creation_enabled)
        .map(fundingArea => fundingArea.fundingProgram?.name)
        .filter(Boolean)
})

const selectableYears = computed(() => (staticStore.years ?? [])
    .filter(year => !restrictsProcessCreation.value || isProcessCreationEnabled(year)))

const formsSelectable = computed(() => !restrictsProcessCreation.value || isProcessCreationEnabled(selectedYear.value))

const selectableFundingPrograms = computed(() => {
    if (!restrictsProcessCreation.value) return fundingPrograms.value

    const enabledIds = enabledFundingAreaIds(selectedYear.value)
    return fundingPrograms.value?.filter(fundingProgram => enabledIds.includes(fundingAreaIdOf(fundingProgram)))
})

const parentFundingPrograms = computed(() => {
    const parentFundingPrograms = selectableFundingPrograms.value?.filter(fundingProgram => fundingProgram.parent_funding_program_id === null)
    const parentFundingProgramIds = parentFundingPrograms.map(fundingProgram => fundingProgram.id)
    selectableFundingPrograms.value?.forEach(fundingProgram => {
        if (fundingProgram.parent_funding_program?.id &&
            !parentFundingProgramIds.includes(fundingProgram.parent_funding_program?.id)) {
            parentFundingPrograms.push(fundingProgram.parent_funding_program)
            parentFundingProgramIds.push(fundingProgram.parent_funding_program?.id)
        }
    })
    return parentFundingPrograms.sort((a, b) => (a.number || 0) - (b.number || 0))
})

const childFundingPrograms = computed(() => {
    return selectableFundingPrograms.value?.filter(fundingProgram => fundingProgram.parent_funding_program_id !== null)
})

const canChangeYear = computed(() => {
    return slideOverStore.context == null
})

onMounted(async () => {
    await staticStore.getStaticData()
    yearId.value = contextYearId.value ?? staticStore.activeYear?.id

    fetchFundingPrograms()
})

function fetchFundingPrograms() {
    if (authStore.isAuthenticated !== true) {
        // todo: reactivate when concept for unauthenticated users is ready
        return
    }

    const params = {
        search: search.value,
        output: 'collectionAll',
        orderBy: 'customOrderSortByParent',
        orderDirection: 'asc',
    }

    if (slideOverStore.context && slideOverStore.context.filters) {
        params.filter = slideOverStore.context.filters
    }

    $larafetch(useNuxtApp().$apiRoute('spa.processes.fundingPrograms.index'), {
        params: {...params}
    }).then(response => {
        fundingPrograms.value = response.data
    }).catch(error => {
        const app = useNuxtApp()
        if (error && error.response?._data.message) {
            notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data.message)
        } else if (error && error.response?._data) {
            notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data)
        } else {
            notification.error(app.$i18n.t('generalRequestError'))
        }
    })
}

function addNewFormProcess(form) {
    loading.value = form.id
    let eventAlreadyExisting = false

    const body = {
        formId: form.id,
        yearId: yearId.value,
        area: slideOverStore.context?.area === 'management' ? 'managementArea' : 'applicationArea'
    }

    if (slideOverStore.context && slideOverStore.context.type) {
        switch (slideOverStore.context.type) {
            case 'event':
                eventAlreadyExisting = true
                body.eventId = slideOverStore.context.id
                break
            case 'process':
                eventAlreadyExisting = true
                body.processId = slideOverStore.context.id
                break
            default:
                break
        }
    }

    $lara.post(useNuxtApp().$apiRoute('spa.processes.formProcesses.store'), {
        body: body,
        formDataRequest: true
    }).then((response) => {
        loading.value = 0
        notification.success($t('formProcesses.createSuccess'))
        if (slideOverStore.context?.area === 'management')
            router.push({
                path: useLocalePath()('/service/formProcesses/' + response.id),
                hash: eventAlreadyExisting ? '#formProcess' : ''
            })
        else
            router.push({
                path: useLocalePath()('/formProcess/' + response.id),
                hash: eventAlreadyExisting ? '#formProcess' : ''
            })
        slideOverStore.resetSlideOver()
    }).catch((error) => {
        loading.value = 0
        console.error(error)
        notification.error($t('formProcesses.createError'), error.data?.errors)
    })
}

watch(() => search.value, () => {
    fetchDebounced()
})

const fetchDebounced = debounce(() => {
    fetchFundingPrograms()
}, 500)
</script>

